import React from 'react'
import { connect } from 'react-redux'
import {
  fetchLicense,
  sendActivationCode,
  rollbackLicense,
  updateLicense,
  fetchLicenseGroupOptions,
} from '../../actions/license-actions'
import { Grid, Row, Col, Button, Alert } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { get, isEmpty, set } from 'lodash'
import moment from 'moment'
import { formatDate, toMoment } from '../../utils/date'

import ConfirmModal from '../../components/ConfirmModal'
import Breadcrumbs from '../../components/Breadcrumbs'
import SearchBar from '../../components/SearchBar'
import ResendActivationCode from '../../components/ResendActivationCode'
import DisplayLabel from '../../components/DisplayLabel'
import Property from '../../components/Property'
import PropertyTitle from '../../components/PropertyTitle'
import NotesOverview from '../../components/NotesOverview'
import PropertyInput from '../../components/PropertyInput'
import NotFound from '../NotFound'

class LicenseDetail extends React.Component {
  constructor() {
    super()
    this.state = { licenseDeactivated: false }
  }

  componentDidMount() {
    this.props.fetchLicense(this.props.match.params.id)
    this.props.fetchLicenseGroupOptions(this.props.match.params.id)
  }

  componentDidUpdate(oldProps) {
    if (this.props.match.params.id !== oldProps.match.params.id) {
      this.props.fetchLicense(this.props.match.params.id)
      this.props.fetchLicenseGroupOptions(this.props.match.params.id)
    }
  }

  resendActivationCode(email) {
    this.props.sendActivationCode(this.props.license.id, email)
  }

  rollback() {
    this.props.rollbackLicense(this.props.license.id).then(() => {
      this.setState({ licenseDeactivated: true })
      this.props.fetchLicense(this.props.license.id)
      this.props.fetchLicenseGroupOptions(this.props.license.id)
    })
  }

  search(query) {
    this.props.history.push(`/licenties?query=${query}`)
  }

  onSave(property, value) {
    let license = this.props.license
    set(license, property, value)
    this.props.updateLicense(license).then(() => {
      this.props.fetchLicense(this.props.match.params.id)
      this.props.fetchLicenseGroupOptions(this.props.match.params.id)
    })
  }

  hasTemporaryAccess() {
    const { license } = this.props
    return !license.navisionId && license.active && toMoment(license.gracePeriodEnd).isAfter(moment())
  }

  render() {
    const { license } = this.props
    if (license && license.id === -1) {
      return <NotFound />
    }
    if (
      isEmpty(license) ||
      (license.id !== parseInt(this.props.match.params.id, 10) && license.activationCode !== this.props.match.params.id)
    ) {
      return null
    }

    return (
      <Grid>
        <Breadcrumbs title={`Licentie ${license.id}`} />
        <SearchBar
          placeholder="Zoek op eigenaar, activatie-code, webshoporder- of navisionnummer"
          callback={(query) => this.search(query)}
        />

        <Row>
          <div className="content">
            <Row>
              <Col xs={12}>
                <span className="status">
                  <b>Licentie</b> {license.status} <DisplayLabel type="STATUS" value={license} />
                </span>
              </Col>
            </Row>
            <Row>
              <Col md={5} sm={12}>
                <PropertyTitle
                  icon="cube"
                  link={`/producten/${get(license, 'product.id')}`}
                  text={get(license, 'product.name')}
                />
                <Property icon="barcode" tooltipText="EAN" value={get(license, 'product.ean')} />
                <Property
                  icon="clock-o"
                  tooltipText="Duur"
                  value={
                    !license.endDate || !license.startDate
                      ? '-'
                        : `${toMoment(license.endDate).diff(
                          toMoment(license.startDate),
                          'months'
                        )} maanden`
                  }
                />
                <Property
                  icon="calendar"
                  tooltipText="Startdatum - Einddatum"
                  value={
                    <span>
                      {formatDate(get(license, 'startDate'))}{' '}
                      {license.endDate && (
                        <span> - {formatDate(get(license, 'endDate'))} </span>
                      )}
                    </span>
                  }
                  entity="license"
                  entityId={license.id}
                  property="startDate,endDate"
                  type="start-end"
                />
                {this.hasTemporaryAccess() && (
                  <Property
                    icon="calendar-check-o"
                    tooltipText="In graceperiode"
                    value={`Voorlopige toegang tot ${formatDate(license.gracePeriodEnd)}`}
                  />
                )}
                <Property
                  icon="chevron-left"
                  tooltipText="Activeren voor"
                  value={
                    !license.activateBefore
                      ? '-'
                      : formatDate(get(license, 'activateBefore'))
                  }
                />
                <Property
                  icon="chevron-right"
                  tooltipText="Activeren na"
                  value={
                    !license.activateFrom
                      ? '-'
                      : formatDate(get(license, 'activateFrom'))
                  }
                />
              </Col>
              <Col md={2} sm={0}></Col>
              <Col md={5} sm={12}>
                <DisplayLabel type="OWNER_TITLE" value={license} />
                <Property
                  icon="money"
                  tooltipText="Credits"
                  value={`${!license.credits ? 0 : license.credits} credits`}
                  entity="license"
                  entityId={license.id}
                  property="credits"
                />
                <Property
                  icon="users"
                  tooltipText="Gelijktijdige gebruikers"
                  value={`${license.maxConcurrentUsers || 0} gelijktijdige gebruikers per publicatie`}
                  entity="license"
                  entityId={license.id}
                  property="users"
                />
                <Property
                  icon="users"
                  tooltipText="Maximaal aantal gebruikers"
                  value={`${license.maxMembers || 0} maximaal aantal gebruikers`}
                  entity="license"
                  entityId={license.id}
                  property="maxMembers"
                />

                <PropertyInput
                  icon="graduation-cap"
                  className="role"
                  label="Rol"
                  type="text"
                  property="role"
                  value={license.role}
                  onSave={(property, value) => this.onSave(property, value)}
                  entity="license"
                  entityId={license.id}
                />
              </Col>
            </Row>
            <Row className="second">
              <Col md={5} sm={12}>
                <a href={license.webshopOrderUrl} target="_blank" rel="noopener noreferrer">
                  <Property icon="external-link" tooltipText="webshoporderid" value={license.webshopOrderItemId} />
                </a>
                <Property
                  icon="file-text-o"
                  tooltipText="NavisionId"
                  value={
                    license.navisionId ? (
                      <Link to={`/order/${license.navisionOrderId}/licenses/${license.navisionId}`}>
                        {license.navisionId}
                      </Link>
                    ) : (
                      '-'
                    )
                  }
                />
                <br></br>
                <span className="extend-period">Verlengen mogelijk in deze periode:</span>
                <Property
                  icon="chevron-left"
                  tooltipText="verlengen na"
                  value={formatDate(license.extendAfter)}
                />

                <Property
                  icon="chevron-right"
                  tooltipText="verlengen voor"
                  value={formatDate(license.extendBefore)}
                />
                {(license.group || license.customer) && (
                  <React.Fragment>
                    <br></br>
                    <PropertyInput
                      icon="users"
                      className="group"
                      label="Groep"
                      type="choice"
                      property="group"
                      value={license.group}
                      choices={[{ id: -1, name: '<<Geen waarde>>' }, ...this.props.groupOptions]}
                      onSave={(property, value) => this.onSave(property, value)}
                      entity="license"
                      entityId={this.props.match.params.id}
                    />
                  </React.Fragment>
                )}
              </Col>
              <Col md={2} sm={0}></Col>
              <Col md={5} sm={12}>
                <span className="activation-code">
                  Activatiecode <b>{license.activationCode}</b>
                </span>
                {this.state.licenseDeactivated && <Alert bsStyle="success">Licentie is ontkoppeld</Alert>}
                {license.state === 'NEW' && (
                  <ResendActivationCode
                    activationCode={license.activationCode}
                    email={license.orderEmail}
                    callback={(email) => this.resendActivationCode(email)}
                  />
                )}
                {license.state === 'ACTIVE' &&
                  license.deactivationPossible &&
                  moment().isBefore(toMoment(license.deactivateBefore)) && (
                    <Button
                      className="rollback"
                      bsStyle="warning"
                      onClick={() => this.confirmModal.show(() => this.rollback())}
                    >
                      Ontkoppelen
                    </Button>
                  )}
              </Col>
            </Row>
            <NotesOverview license={license} />
          </div>
        </Row>
        <ConfirmModal
          ref={(ref) => (this.confirmModal = ref)}
          message="Weet je zeker dat je deze licentie wilt ontkoppelen?"
        />
      </Grid>
    )
  }
}

export default connect(
  (state) => {
    return { license: state.licenses.detail.license, groupOptions: state.licenses.detail.groupOptions || [] }
  },
  {
    fetchLicense,
    sendActivationCode,
    rollbackLicense,
    updateLicense,
    fetchLicenseGroupOptions,
  }
)(LicenseDetail)
